import type { MessagesChannelInterface } from '@interfaces/messages'
import type { ActionType } from './action'
import AppReducer, { initialState as appInitialState, type AppStateType } from './app'
import AuthReducer, { initialState as authInitialState, type AuthStateType } from './auth'
import AutomationEmailsReducer, {
  initialState as automationEmailsInitialState,
  type AutomationEmailsStateType,
} from './automation_emails'
import DownloadsReducer, {
  initialState as downloadInitialState,
  type DownloadsStateType,
} from './downloads'
import LayoutReducer, { initialState as layoutInitialState, type LayoutStateType } from './layout'
import LayoutsReducer, {
  initialState as layoutsInitialState,
  type LayoutsStateType,
} from './layouts'
import MessagesReducer, { initialState as messagesInitialState } from './messages'
import OrderReducer, {
  initialState as orderInitialState,
  type OrderReducerInterface,
} from './order'
import PageFormReducer, {
  initialState as pageFormInitialState,
  type PageFormStateType,
} from './page_form'
import ShopReducer, { initialState as shopInitialState, type ShopStateType } from './shop'
import ThemesReducer, { initialState as themesInitialState, type ThemeStateTyppe } from './themes'

export interface AppContextType {
  LayoutReducer: LayoutStateType
  ShopReducer: ShopStateType
  LayoutsReducer: LayoutsStateType
  PageFormReducer: PageFormStateType
  ThemesReducer: ThemeStateTyppe
  AuthReducer: AuthStateType
  OrderReducer: OrderReducerInterface
  MessagesReducer: MessagesChannelInterface
  DownloadsReducer: DownloadsStateType
  AppReducer: AppStateType
  AutomationEmailsReducer: AutomationEmailsStateType
}

export const rootInitialState: AppContextType = {
  LayoutReducer: layoutInitialState,
  ShopReducer: shopInitialState,
  LayoutsReducer: layoutsInitialState,
  PageFormReducer: pageFormInitialState,
  ThemesReducer: themesInitialState,
  AuthReducer: authInitialState,
  OrderReducer: orderInitialState,
  MessagesReducer: messagesInitialState,
  DownloadsReducer: downloadInitialState,
  AppReducer: appInitialState,
  AutomationEmailsReducer: automationEmailsInitialState,
}

type ReducerMap<T> = {
  [K in keyof T]: (state: T[K], action: ActionType) => T[K]
}

// Memoize the combineReducers function to prevent recreation
const combineReducers =
  (slices: ReducerMap<AppContextType>) => (state: AppContextType, action: ActionType) =>
    Object.keys(slices).reduce(
      (acc, prop) => {
        const key = prop as keyof AppContextType
        acc[key] = slices[key](acc[key], action)
        return acc
      },
      { ...state } as AppContextType
    )

// Create the root reducer once
export const rootReducer = combineReducers({
  LayoutReducer,
  ShopReducer,
  AppReducer,
  LayoutsReducer,
  PageFormReducer,
  ThemesReducer,
  AuthReducer,
  OrderReducer,
  MessagesReducer,
  DownloadsReducer,
  AutomationEmailsReducer,
})
